"use client";

import { CalendarClock, MapPin, Stethoscope, User } from "lucide-react";
import { useState } from "react";

import type { AvailabilityAppointmentSlot } from "@/shared";
import { SchedulingErrorAlert } from "@/src/components/online-scheduling/scheduling-error-alert";
import { StepActions } from "@/src/components/online-scheduling/step-actions";
import { SummaryPanel, SummaryRow } from "@/src/components/online-scheduling/summary";
import {
  formatLocality,
  isMinorDateOfBirth,
  type OnlineSchedulingLocation,
  type PatientDetailsDraft,
} from "@/src/components/online-scheduling/types";
import { Alert, AlertDescription, AlertTitle } from "@/src/components/shadcn/ui/alert";
import { Checkbox } from "@/src/components/shadcn/ui/checkbox";
import { Label } from "@/src/components/shadcn/ui/label";
import type { OnlineSchedulingExistingAppointment } from "@/src/lib/scheduling/online-scheduling-schemas";
import { formatSlotDateTime } from "@/src/lib/scheduling/slot-display";

export function ReviewStep({
  location,
  isReturningPatient,
  reasonForVisit,
  slot,
  details,
  onBack,
  onConfirm,
  isBooking,
  bookingError,
  clinicPhoneNumber,
  previousAppointment,
  rescheduleNeedsStaff = false,
}: {
  location: OnlineSchedulingLocation;
  isReturningPatient: boolean;
  reasonForVisit: string;
  slot: AvailabilityAppointmentSlot;
  details: PatientDetailsDraft;
  onBack: () => void;
  onConfirm: (smsConsent: boolean) => void;
  isBooking: boolean;
  bookingError: string | null;
  clinicPhoneNumber?: string | null;
  previousAppointment?: OnlineSchedulingExistingAppointment | null;
  rescheduleNeedsStaff?: boolean;
}) {
  const locality = formatLocality(location);
  const [hasConsented, setHasConsented] = useState(false);

  return (
    <div className="space-y-6">
      <SummaryPanel>
        {previousAppointment && (
          <SummaryRow icon={<CalendarClock className="h-4 w-4" />} label="Current appointment">
            <p>{formatSlotDateTime(previousAppointment.start)}</p>
            <p className="text-muted-foreground">This appointment will be moved to the new time below.</p>
          </SummaryRow>
        )}
        <SummaryRow icon={<CalendarClock className="h-4 w-4" />} label="Appointment">
          <p className="font-medium">{formatSlotDateTime(slot.start)}</p>
          {slot.practitionerDisplayName && <p className="text-muted-foreground">with {slot.practitionerDisplayName}</p>}
        </SummaryRow>
        <SummaryRow icon={<MapPin className="h-4 w-4" />} label="Location">
          <p className="font-medium">{location.displayName}</p>
          {location.addressLine1 && <p className="text-muted-foreground">{location.addressLine1}</p>}
          {locality && <p className="text-muted-foreground">{locality}</p>}
        </SummaryRow>
        <SummaryRow icon={<Stethoscope className="h-4 w-4" />} label="Visit">
          <p className="font-medium">
            {previousAppointment?.appointmentTypeName ?? (isReturningPatient ? "Returning patient" : "New patient")}
          </p>
          {!previousAppointment && reasonForVisit.trim() && (
            <p className="text-muted-foreground">{reasonForVisit.trim()}</p>
          )}
        </SummaryRow>
        <SummaryRow icon={<User className="h-4 w-4" />} label="Patient">
          <p className="font-medium">
            {details.firstName} {details.lastName}
          </p>
          <p className="text-muted-foreground">DOB {details.dateOfBirth}</p>
          {!previousAppointment && details.gender && <p className="text-muted-foreground">{details.gender}</p>}
          <p className="text-muted-foreground">{details.phone}</p>
          {!previousAppointment && (
            <>
              <p className="break-words text-muted-foreground">
                {[details.homeAddress.line1, details.homeAddress.line2].filter(Boolean).join(", ")}
              </p>
              <p className="text-muted-foreground">
                {details.homeAddress.city}, {details.homeAddress.state} {details.homeAddress.postalCode}
              </p>
            </>
          )}
          {details.selfPay && <p className="text-muted-foreground">Self-pay (without insurance)</p>}
          {!details.selfPay && (details.insurancePayer.trim() || details.insuranceMemberId.trim()) && (
            <p className="text-muted-foreground">
              {details.insurancePayer.trim() || "Insurance"}
              {details.insuranceMemberId.trim() ? ` · #${details.insuranceMemberId.trim()}` : ""}
            </p>
          )}
          {isMinorDateOfBirth(details.dateOfBirth) &&
            (details.guardianFirstName.trim() || details.guardianLastName.trim() || details.guardianPhone.trim()) && (
              <p className="text-muted-foreground">
                Guardian:{" "}
                {[details.guardianFirstName.trim(), details.guardianLastName.trim()].filter(Boolean).join(" ")}
                {details.guardianPhone.trim() ? ` · ${details.guardianPhone.trim()}` : ""}
              </p>
            )}
        </SummaryRow>
      </SummaryPanel>

      {bookingError &&
        (previousAppointment && !rescheduleNeedsStaff ? (
          <Alert variant="destructive">
            <AlertTitle>Appointment not changed</AlertTitle>
            <AlertDescription>{bookingError}</AlertDescription>
          </Alert>
        ) : (
          <SchedulingErrorAlert clinicPhoneNumber={clinicPhoneNumber} />
        ))}
      {rescheduleNeedsStaff && (
        <p role="alert" className="text-sm">
          {bookingError}
        </p>
      )}

      <div className="flex items-center gap-2">
        <Checkbox
          id="contact-consent"
          checked={hasConsented}
          onCheckedChange={(checked) => setHasConsented(checked === true)}
          disabled={isBooking}
        />
        <Label htmlFor="contact-consent" className="text-xs font-normal leading-snug text-muted-foreground">
          By confirming, you agree to be contacted about this appointment over phone or email.
        </Label>
      </div>

      <StepActions
        primaryLabel={
          previousAppointment
            ? isBooking
              ? "Rescheduling your appointment..."
              : "Confirm reschedule"
            : isBooking
              ? "Booking your appointment..."
              : "Confirm appointment"
        }
        onPrimary={() => onConfirm(hasConsented)}
        primaryDisabled={!hasConsented || rescheduleNeedsStaff}
        isBusy={isBooking}
        onBack={onBack}
        backDisabled={isBooking || rescheduleNeedsStaff}
      />
    </div>
  );
}
